import { Check } from "lucide-react";
import { StatusBadge } from "@/shared/components/settings-atoms";
import { PLANS } from "@/shared/lib/plans";
import { formatCredits, shortDate } from "./format";

import type { PlanId, PlanTier } from "@/shared/types";
import type { BillingPlan } from "@/shared/types/billing";

/** The copy is ours (`PLANS`), the price and credits billing's (`price`, absent while loading).
    A plan above the current one asks to upgrade (dark); one below, to switch (quiet). */
export default function PlanCard({ plan, price, currentId, scheduledAt, disabled, onSelect }: {
  plan: PlanTier;
  price: BillingPlan | undefined;
  currentId: PlanId;
  /** When a scheduled change moves the org onto this plan. */
  scheduledAt?: string;
  /** No billing.manage, or a write in flight. */
  disabled: boolean;
  onSelect: () => void;
}) {
  const current = plan.id === currentId;
  const rank = (id: PlanId) => PLANS.findIndex((p) => p.id === id);
  const up = rank(plan.id) > rank(currentId);
  return (
    <div
      className={`flex flex-col rounded-[14px] p-5 bg-[var(--surface)] ring-1 transition-shadow duration-200 ${
        current
          ? "ring-[var(--hairline-focus)] shadow-[0_8px_24px_-12px_rgba(0,0,0,0.16)]"
          : "ring-[var(--hairline)] shadow-lift card-hover"
      }`}
    >
      <div className="flex items-center justify-between gap-2 min-h-5">
        <span className="text-[14px] font-semibold tracking-[-0.01em] text-[var(--text-primary)]">{plan.name}</span>
        {current ? <StatusBadge>Current</StatusBadge> : scheduledAt ? <StatusBadge tone="warn">Scheduled</StatusBadge> : plan.id === "pro" && <StatusBadge tone="success">Best value</StatusBadge>}
      </div>
      <div className="mt-3 flex items-baseline gap-1.5">
        <span className="text-[28px] leading-none font-semibold tracking-[-0.02em] tabular-nums text-[var(--text-primary)]">
          {price ? `$${price.amount}` : "—"}
        </span>
        <span className="text-[12.5px] text-[var(--text-tertiary)]">/ month</span>
      </div>
      <p className="m-0 mt-2 text-[12.5px] leading-[1.5] text-[var(--text-tertiary)]">{plan.tagline}</p>
      <ul className="m-0 mt-4 mb-5 pt-4 p-0 list-none space-y-2 border-t hairline">
        {[...(price ? [`${formatCredits(price.credits)} credits / month`] : []), ...plan.features].map((f) => (
          <li key={f} className="flex items-start gap-2 text-[12.5px] leading-[1.45] text-[var(--text-secondary)]">
            <Check className="w-3.5 h-3.5 mt-[1px] shrink-0 text-[var(--text-tertiary)]" strokeWidth={2} />
            {f}
          </li>
        ))}
      </ul>
      <button
        disabled={current || !!scheduledAt || disabled || !price}
        onClick={onSelect}
        className={`press mt-auto w-full rounded-[10px] h-8 text-[13px] font-medium disabled:pointer-events-none ${
          current
            ? "ring-1 ring-[var(--hairline)] text-[var(--text-quaternary)] cursor-default"
            : up
              ? "bg-[var(--brand)] text-[var(--brand-foreground)] hover:bg-[var(--brand-hover)] shadow-button disabled:opacity-40"
              : "bg-[var(--surface)] text-[var(--text-primary)] ring-1 ring-[var(--hairline-strong)] shadow-lift hover:bg-[var(--surface-sunk)] disabled:opacity-40"
        }`}
      >
        {current ? "Current plan" : scheduledAt ? `Starts ${shortDate(scheduledAt)}` : up ? `Upgrade to ${plan.name}` : `Switch to ${plan.name}`}
      </button>
    </div>
  );
}
